import { loadQuestionsFromDB, getQuestionCountFromDB } from "./questionService";

export type QuestionSource = "database" | "static";

export interface Question {
  id: number | string;
  passage?: string;
  passageImage?: string;
  question: string;
  questionImage?: string;
  image?: string;
  options: {
    id: string;
    text: string;
    isCorrect: boolean;
  }[];
  explanation: string;
  category?: string;
  difficulty?: "easy" | "medium" | "hard";
}

// Flag to enable/disable database loading (set to true when DB is ready)
const USE_DATABASE = true;

/**
 * Load questions and return both questions and where they came from.
 */
export async function loadQuestionsWithSource(
  subjectId: string,
  moduleId: string,
  options?: {
    topicId?: string;
    subtopicId?: string;
    difficulty?: "easy" | "medium" | "hard";
    shuffle?: boolean;
  }
): Promise<{ questions: Question[]; source: QuestionSource }> {
  // Try database first if enabled
  if (USE_DATABASE) {
    try {
      const dbQuestions = await loadQuestionsFromDB(subjectId, moduleId, {
        topicId: options?.topicId,
        subtopicId: options?.subtopicId,
        difficulty: options?.difficulty,
        shuffle: options?.shuffle,
      });

      if (dbQuestions.length > 0) {
        console.log(
          `📦 Loaded ${dbQuestions.length} questions from Appwrite for ${subjectId}/${moduleId}`
        );
        return { questions: dbQuestions, source: "database" };
      }
    } catch (error) {
      console.warn("Database load failed, falling back to JSON:", error);
    }
  }

  // Fallback to static JSON files
  const staticQuestions = await loadQuestionsFromJSON(
    subjectId,
    moduleId,
    options?.topicId,
    options?.difficulty,
    options?.subtopicId
  );
  return { questions: staticQuestions, source: "static" };
}

/**
 * Load questions - tries Appwrite database first, falls back to JSON files
 */
export async function loadQuestions(
  subjectId: string,
  moduleId: string,
  options?: {
    topicId?: string;
    subtopicId?: string;
    difficulty?: "easy" | "medium" | "hard";
    shuffle?: boolean;
  }
): Promise<Question[]> {
  const { questions } = await loadQuestionsWithSource(subjectId, moduleId, options);
  return questions;
}

/**
 * Fetch a question file from the API by key.
 * Returns null if not found.
 */
async function fetchQuestionFile(key: string): Promise<Question[] | null> {
  try {
    const res = await fetch(`/api/questions/${key}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.found) return null;
    return data.questions as Question[];
  } catch {
    return null;
  }
}

/**
 * Load questions from static JSON files via API.
 *
 * Resolution order (most specific → least specific):
 *   1. {subject}_{module}_{topic}_{subtopic}_{difficulty}.json
 *   2. {subject}_{module}_{topic}_{subtopic}.json  — filtered by difficulty
 *   3. {subject}_{module}_{topic}.json             — filtered by skill + difficulty
 *   4. {subject}_{module}.json                     — filtered by category + difficulty
 */
async function loadQuestionsFromJSON(
  subjectId: string,
  moduleId: string,
  topicId?: string,
  difficulty?: "easy" | "medium" | "hard",
  subtopicId?: string
): Promise<Question[]> {
  let questions: Question[] | null = null;

  if (topicId && subtopicId && difficulty) {
    // 1. Exact subtopic+difficulty file
    const key = `${subjectId}_${moduleId}_${topicId}_${subtopicId}_${difficulty}`;
    questions = await fetchQuestionFile(key);
    if (questions) console.log(`📄 Loaded from ${key}.json`);
  }

  if (!questions && topicId && subtopicId) {
    // 2. Subtopic file (all difficulties), then filter
    const key = `${subjectId}_${moduleId}_${topicId}_${subtopicId}`;
    questions = await fetchQuestionFile(key);
    if (questions) console.log(`📄 Loaded from ${key}.json`);
  }

  if (!questions && topicId) {
    // 3. Topic file, filter by skill
    const key = `${subjectId}_${moduleId}_${topicId}`;
    questions = await fetchQuestionFile(key);
    if (questions) {
      console.log(`📄 Loaded from ${key}.json`);
      if (subtopicId) {
        const normalized = subtopicId.toLowerCase().replace(/_/g, " ");
        questions = questions.filter(
          (q) => (q as any).skill?.toLowerCase().replace(/_/g, " ") === normalized
        );
      }
    }
  }

  if (!questions) {
    // 4. Module file, filter by category
    const key = `${subjectId}_${moduleId}`;
    questions = await fetchQuestionFile(key);
    if (questions) {
      console.log(`📄 Loaded from ${key}.json`);
      if (topicId) {
        const normalized = topicId.toLowerCase().replace(/_/g, " ");
        questions = questions.filter(
          (q) => q.category?.toLowerCase().replace(/_/g, " ") === normalized
        );
      }
    }
  }

  if (!questions || questions.length === 0) {
    console.warn(`No questions found for ${subjectId}/${moduleId}${topicId ? `/${topicId}` : ""}${subtopicId ? `/${subtopicId}` : ""}`);
    return [];
  }

  if (difficulty) {
    questions = questions.filter((q) => q.difficulty === difficulty);
  }

  return questions;
}

/**
 * Get question count - tries database first, falls back to API
 */
export async function getQuestionCount(
  subjectId: string,
  moduleId: string,
  topicId?: string
): Promise<number> {
  if (USE_DATABASE) {
    try {
      const count = await getQuestionCountFromDB(subjectId, moduleId, topicId);
      if (count > 0) return count;
    } catch {
      console.warn("Database count failed, using fallback");
    }
  }

  const questions = await loadQuestionsFromJSON(subjectId, moduleId, topicId);
  return questions.length;
}
